import { useCallback, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { StudyParticipantServiceApiClient } from "apiclient/StudyParticipantServiceApiClient";
import { StudySiteServiceApiClient } from "apiclient/StudySiteServiceApiClient";
import { ChangeLogRow } from "common/ChangeLogRow";
import { getParticipantStatus } from "../studyparticipant/StudyParticipants";
import { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb";
import { WhichChangeLogsTable } from "./ChangeLogsStateSlice";
import { ComplianceSettingServiceApiClient } from "apiclient/ComplianceSettingServiceApiClient";
import { StudyParticipantChangeLog } from "generated/studyparticipant/studyparticipant_pb";
import { StudyDetailServiceApiClient } from "apiclient/StudyDetailServiceApiClient";
import { StudyAuthServiceApiClient } from "apiclient/StudyAuthServiceApiClient";
import { ComplianceSettingChangeLog } from "generated/compliancesetting/compliancesetting_pb";
import { StudySiteChangeLog } from "generated/studysite/studysite_pb";
import { StudyDetailChangeLog } from "generated/studydetail/studydetail_pb";
import {
  AccountType,
  InvitationStatus,
  Role,
  UserChangeLog,
} from "generated/studyauth/studyauth_pb";
import { FieldChangeLog } from "generated/common/fieldchangelog_pb";
import { telemetry, userNotAuthenticated } from "core/TelemetryController";

function convertFieldToDisplayString(field: string): string {
  switch (field) {
    case "SponsorParticipantId":
      return "Participant ID";
    case "StartDate":
      return "Enrollment Date";
    case "ExitStudyDate":
      return "Exit Study Date";
    case "DeviceId":
      return "Device ID";
    case "StudyId":
      return "Study ID";
    case "CompletedDate":
      return "Completed Date";
    case "WithdrawnDate":
      return "Withdrawn Date";
    case "WearTimeDaysPerWeek":
      return "Wear Time Days Per Week";
    case "WearTimeHoursPerDay":
      return "Wear Time Hours Per Day";
    case "SyncDaysPerWeek":
      return "Sync Days Per Week";
    case "SurveySubmissionPercentage":
      return "Survey Submission Percentage";
    case "MedsLoggedPerDay":
      return "Meds Logged Per Day";
    case "MedsLoggedPerWeek":
      return "Meds Logged Per Week";
    case "FirstName":
      return "First Name";
    case "LastName":
      return "Last Name";
    case "EmailAddress":
      return "Email Address";
    case "InvitationStatus":
      return "Invitation Status";
    case "AccountType":
      return "Account Type";
    case "UserRole":
      return "User Role";
    case "StudyName":
      return "Study Name";
    case "SponsorName":
      return "Sponsor Name";
    case "VerilyCDM":
      return "Verily DM";
    case "SiteName":
      return "Site Name";
    case "SiteNumber":
      return "Site Number";
    case "PrincipalInvestigator":
      return "Principal Investigator";
    case "CountryCode":
      return "Country";
    case "ArchivedDate":
      return "Archived Date";
    case "ECGReadingsPerDay":
      return "ECG Readings Per Day";
    default:
      return field;
  }
}
function convertOldOrNewValueToDisplayString(
  field: string,
  oldOrNewValue: string,
  whichChangeLogsTable: WhichChangeLogsTable
): string {
  if (oldOrNewValue === "0000-00-00") {
    return "--";
  }
  if (oldOrNewValue === "--" || oldOrNewValue === "") {
    return oldOrNewValue;
  } else if (field === "Status") {
    if (whichChangeLogsTable === WhichChangeLogsTable.STUDY_SITE) {
      switch (oldOrNewValue) {
        case "0":
          return "Active";
        case "1":
          return "Archived";
        case "":
          return "Deleted";
        default:
          return oldOrNewValue;
      }
    }

    return getParticipantStatus(parseInt(oldOrNewValue)) || "";
  } else if (field === "InvitationStatus") {
    switch (oldOrNewValue) {
      case InvitationStatus.INVITATION_STATUS_UNSPECIFIED.toString():
        return "Unspecified";
      case InvitationStatus.INVITE_FAILED.toString():
        return "Invite Failed";
      case InvitationStatus.INVITED.toString():
        return "Invited";
      case InvitationStatus.ACCEPTED.toString():
        return "Accepted";
      default:
        return oldOrNewValue;
    }
  } else if (field === "AccountType") {
    switch (oldOrNewValue) {
      case AccountType.ACCOUNT_TYPE_UNSPECIFIED.toString():
        return "Unspecified";
      case AccountType.EMAIL_PASSWORD.toString():
        return "Email Password";
      case AccountType.ENTERPRISE.toString():
        return "Enterprise";
      default:
        return oldOrNewValue;
    }
  } else if (field === "UserRole") {
    switch (oldOrNewValue) {
      case Role.ROLE_UNSPECIFIED.toString():
        return "Unspecified";
      case Role.DEV_OPS.toString():
        return "DevOps";
      case Role.SPONSOR_OPS.toString():
        return "SponsorOps";
      case Role.STUDY_MONITOR.toString():
        return "Study Monitor";
      case Role.PARTICIPANT_MONITOR.toString():
        return "Participant Monitor";
      case Role.COMPLIANCE_ADMIN.toString():
        return "Compliance Admin";
      case Role.CDM.toString():
        return "DM";
      case Role.CRC.toString():
        return "Site Research Coordinator";
      default:
        return oldOrNewValue;
    }
  } else if (field === "CountryCode") {
    switch (oldOrNewValue) {
      case "CA":
        return "Canada";
      case "US":
        return "United States";
      case "AT":
        return "Austria";
      case "FR":
        return "France";
      case "DE":
        return "Germany";
      case "IT":
        return "Italy";
      case "NL":
        return "Netherlands";
      case "PL":
        return "Poland";
      case "ES":
        return "Spain";
      case "GB":
        return "United Kingdom";
      case "JP":
        return "Japan";
      case "AR":
        return "Argentina";
      case "IL":
        return "Israel";
      default:
        return "United States";
    }
  }
  return oldOrNewValue;
}

// Defines a custom effect to load data change logs for an entire study site.
function useChangeLogsDataLoader(
  studySiteId: string,
  participantId?: string,
  whichChangeLogsTable?: WhichChangeLogsTable,
  registryId?: string
) {
  const auth0Config = useAppSelector((state) => state.auth0Config);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [changeLogRows, setChangeLogRows] = useState(new Array<ChangeLogRow>());
  const [idToNameMap, setIdtoNameMap] = useState(new Map<string, string>());

  const [uniqueUsers, setUniqueUsers] = useState(new Array<string>());
  const [uniqueSites, setUniqueSites] = useState(new Array<string>());

  const loadChangeLogRows = useCallback(() => {
    const optionalFields = [
      "Completed Date",
      "Withdrawn Date",
      "Meds Logged Per Day",
      "Meds Logged Per Week",
      "ECG Readings Per Day",
      "Survey Submission Percentage",
      "Study ID",
      "Site Number",
      "Principal Investigator",
      "Country",
      "Archived Date",
    ];
    if (isAuthenticated && auth0Config) {
      (async () => {
        setHasError(false);
        setIsLoading(true);

        // load study participant change logs
        const token = await getAccessTokenSilently({
          audience: auth0Config.audience,
        });

        try {
          var compressedChangeLogs: any[] = [];
          if (
            whichChangeLogsTable === WhichChangeLogsTable.COMPLIANCE_SETTINGS
          ) {
            if (registryId === "") {
              return;
            }
            const client = new ComplianceSettingServiceApiClient(
              auth0Config.audience!,
              token
            );
            const complianceSettingChangeLogsResponse =
              await client.listComplianceSettingChangeLogs(registryId!);
            compressedChangeLogs =
              complianceSettingChangeLogsResponse.getComplianceSettingHistoriesChangeLogsList();
          } else if (
            whichChangeLogsTable ===
              WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_STUDY_SITE_ID ||
            whichChangeLogsTable ===
              WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID ||
            whichChangeLogsTable ===
              WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID
          ) {
            const studyParticipantServiceClient =
              new StudyParticipantServiceApiClient(
                auth0Config.audience!,
                token
              );

            if (
              whichChangeLogsTable ===
              WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_STUDY_SITE_ID
            ) {
              if (studySiteId === "") {
                return;
              }
              const studyParticipantChangeLogsResponse =
                await studyParticipantServiceClient.listStudyParticipantsChangeLogsByStudySiteId(
                  studySiteId
                );
              compressedChangeLogs =
                studyParticipantChangeLogsResponse.getStudyParticipantChangeLogsList();
            } else if (
              whichChangeLogsTable ===
              WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID
            ) {
              if (participantId === "") {
                return;
              }
              const studyParticipantChangeLogsResponse =
                await studyParticipantServiceClient.listStudyParticipantChangeLogsByStudyParticipantId(
                  participantId!
                );
              compressedChangeLogs =
                studyParticipantChangeLogsResponse.getStudyParticipantChangeLogsList();
            } else if (
              whichChangeLogsTable ===
              WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID
            ) {
              if (registryId === "") {
                return;
              }
              const studyParticipantChangeLogsResponse =
                await studyParticipantServiceClient.listStudyParticipantsChangeLogsByStudy(
                  registryId!
                );
              compressedChangeLogs =
                studyParticipantChangeLogsResponse.getStudyParticipantChangeLogsList();
            }
          } else if (whichChangeLogsTable === WhichChangeLogsTable.STUDY_SITE) {
            const studySiteServiceClient = new StudySiteServiceApiClient(
              auth0Config.audience!,
              token
            );
            const studySiteChangeLogsResponse =
              await studySiteServiceClient.listStudySitesChangeLogs(
                registryId!
              );
            compressedChangeLogs =
              studySiteChangeLogsResponse.getStudySiteChangeLogsList();
          } else if (
            whichChangeLogsTable === WhichChangeLogsTable.STUDY_DETAILS
          ) {
            const studyDetailServiceClient = new StudyDetailServiceApiClient(
              auth0Config.audience!,
              token
            );
            const studyDetailChangeLogsResponse =
              await studyDetailServiceClient.listStudyDetailsChangeLogs(
                registryId!
              );
            compressedChangeLogs =
              studyDetailChangeLogsResponse.getStudyDetailsChangeLogsList();
          } else if (
            whichChangeLogsTable ===
            WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW
          ) {
            const userServiceClient = new StudyAuthServiceApiClient(
              auth0Config.audience!,
              token
            );
            const userChangeLogsResponse =
              await userServiceClient.listUsersChangeLogsByStudy(registryId!);
            compressedChangeLogs =
              userChangeLogsResponse.getUsersChangeLogsList();
          } else if (
            whichChangeLogsTable ===
            WhichChangeLogsTable.USERS_COMPLIANCE_ADMIN_VIEW
          ) {
            const userServiceClient = new StudyAuthServiceApiClient(
              auth0Config.audience!,
              token
            );
            const userChangeLogsResponse =
              await userServiceClient.listAllUsersChangeLogs();
            compressedChangeLogs =
              userChangeLogsResponse.getUsersChangeLogsList();
          }

          var idToNameMapLocal = new Map<string, string>();

          var changeLogRowsLocal: ChangeLogRow[] = [];
          const uniqueUsersLocal = new Set<string>();
          const uniqueSitesLocal = new Set<string>();
          compressedChangeLogs.sort((a, b) => {
            const timeA = a
              .getHistoryInformation()
              .getUpdatedTime()
              .getSeconds();
            const timeB = b
              .getHistoryInformation()
              .getUpdatedTime()
              .getSeconds();
            return timeA - timeB;
          });
          for (let i = 0; i < compressedChangeLogs.length; i++) {
            var compressedChangeLog = compressedChangeLogs[i];
            for (
              let j = 0;
              j < compressedChangeLog.getFieldChangeLogsList().length;
              j++
            ) {
              const fieldChangeLog: FieldChangeLog =
                compressedChangeLog.getFieldChangeLogsList()[
                  j
                ] as FieldChangeLog;
              if (
                compressedChangeLog instanceof StudyParticipantChangeLog &&
                fieldChangeLog.getField() === "SponsorParticipantId"
              ) {
                const currentName =
                  fieldChangeLog.getNewValue() !== "--"
                    ? fieldChangeLog.getNewValue()
                    : fieldChangeLog.getOldValue();

                idToNameMapLocal.set(
                  compressedChangeLog.getStudyParticipantId(),
                  currentName
                );
              } else if (
                compressedChangeLog instanceof StudySiteChangeLog &&
                fieldChangeLog.getField() === "SiteName"
              ) {
                const currentName =
                  fieldChangeLog.getNewValue() !== "--"
                    ? fieldChangeLog.getNewValue()
                    : fieldChangeLog.getOldValue();

                idToNameMapLocal.set(
                  compressedChangeLog.getStudySiteId(),
                  currentName
                );
              } else if (
                compressedChangeLog instanceof UserChangeLog &&
                fieldChangeLog.getField() === "EmailAddress"
              ) {
                const currentName =
                  fieldChangeLog.getNewValue() !== "--"
                    ? fieldChangeLog.getNewValue()
                    : fieldChangeLog.getOldValue();

                const userId = compressedChangeLog.getName().split("/").pop();
                idToNameMapLocal.set(userId || "", currentName);
              }
            }

            uniqueUsersLocal.add(
              compressedChangeLog.getHistoryInformation()?.getActorEmail() || ""
            );
            if (
              compressedChangeLog instanceof StudyParticipantChangeLog &&
              compressedChangeLog.getSiteName() !== undefined &&
              compressedChangeLog.getSiteName() !== ""
            ) {
              uniqueSitesLocal.add(compressedChangeLog.getSiteName());
            }
            var humanReadableFieldChangeLogList = Array.from(
              compressedChangeLog
                .getFieldChangeLogsList()
                .filter((fieldChangeLog: FieldChangeLog) => {
                  return (
                    fieldChangeLog.getOldValue() !==
                    fieldChangeLog.getNewValue()
                  );
                })
                .map((fieldChangeLog: FieldChangeLog) => {
                  const newFieldChangeLog = new FieldChangeLog();
                  newFieldChangeLog.setField(
                    convertFieldToDisplayString(fieldChangeLog.getField())
                  );
                  newFieldChangeLog.setOldValue(
                    convertOldOrNewValueToDisplayString(
                      fieldChangeLog.getField(),
                      fieldChangeLog.getOldValue(),
                      whichChangeLogsTable || WhichChangeLogsTable.STUDY_DETAILS
                    )
                  );
                  newFieldChangeLog.setNewValue(
                    convertOldOrNewValueToDisplayString(
                      fieldChangeLog.getField(),
                      fieldChangeLog.getNewValue(),
                      whichChangeLogsTable || WhichChangeLogsTable.STUDY_DETAILS
                    )
                  );
                  return newFieldChangeLog;
                })
            ).sort((a, b) => {
              if (a instanceof FieldChangeLog && b instanceof FieldChangeLog) {
                const fieldA = a.getField();
                const fieldB = b.getField();

                const isFieldAOptional = optionalFields.includes(fieldA);
                const isFieldBOptional = optionalFields.includes(fieldB);

                if (isFieldAOptional && !isFieldBOptional) {
                  return 1;
                } else if (!isFieldAOptional && isFieldBOptional) {
                  return -1;
                } else {
                  return fieldA.localeCompare(fieldB);
                }
              }
              return 0;
            });
            for (let i = 0; i < humanReadableFieldChangeLogList.length; i++) {
              const fieldChangeLog: FieldChangeLog =
                humanReadableFieldChangeLogList[i] as FieldChangeLog;
              const newLog: ChangeLogRow = {
                // eslint-disable-next-line no-loop-func
                IdNumber: (() => {
                  if (
                    compressedChangeLog instanceof StudyParticipantChangeLog
                  ) {
                    return compressedChangeLog.getStudyParticipantId();
                  } else if (
                    compressedChangeLog instanceof ComplianceSettingChangeLog
                  ) {
                    return (
                      compressedChangeLog.getComplianceSetting()?.getName() ||
                      ""
                    );
                  } else if (
                    compressedChangeLog instanceof StudySiteChangeLog
                  ) {
                    return compressedChangeLog.getStudySiteId();
                  } else if (
                    compressedChangeLog instanceof StudyDetailChangeLog
                  ) {
                    return compressedChangeLog.getRegistryId();
                  } else if (compressedChangeLog instanceof UserChangeLog) {
                    return compressedChangeLog.getName();
                  } else {
                    return "";
                  }
                })(),
                Operation:
                  compressedChangeLog.getHistoryInformation()?.getOperation() ||
                  "",
                Field: fieldChangeLog.getField(),
                OldValue: fieldChangeLog.getOldValue(),
                NewValue: fieldChangeLog.getNewValue(),
                UpdatedTime:
                  compressedChangeLog
                    .getHistoryInformation()
                    ?.getUpdatedTime() || new Timestamp(),
                UpdateReason:
                  compressedChangeLog
                    .getHistoryInformation()
                    ?.getUpdateReason() || "",
                ActorEmail:
                  compressedChangeLog
                    .getHistoryInformation()
                    ?.getActorEmail() || "",
                Version:
                  compressedChangeLog.getHistoryInformation()?.getVersion() ||
                  0,
                Site:
                  compressedChangeLog instanceof StudyParticipantChangeLog
                    ? compressedChangeLog.getSiteName()
                    : "",
                SimultaneousFieldChangeLogs:
                  i === 0
                    ? (humanReadableFieldChangeLogList as FieldChangeLog[])
                    : [],
              };
              newLog.isFlagshipForSimultaneousChanges = i === 0;
              changeLogRowsLocal.push(newLog);
            }
          }

          setIdtoNameMap(idToNameMapLocal);
          setChangeLogRows(changeLogRowsLocal);
          setUniqueUsers(Array.from(uniqueUsersLocal).sort());
          setUniqueSites(Array.from(uniqueSitesLocal).sort());
        } catch (error) {
          telemetry.logError(error);
          setHasError(true);
        }

        setIsLoading(false);
      })();
    } else {
      telemetry.logError(new Error(userNotAuthenticated));
      setHasError(true);
      setIsLoading(false);
    }
  }, [
    isAuthenticated,
    auth0Config,
    getAccessTokenSilently,
    whichChangeLogsTable,
    registryId,
    studySiteId,
    participantId,
  ]);

  return {
    isLoading,
    hasError,
    changeLogRows,
    idToNameMap,
    uniqueUsers,
    uniqueSites,
    loadChangeLogRows,
  };
}
export default useChangeLogsDataLoader;
